import { useTranslation } from 'react-i18next';

interface HighlightItem {
  icon: string;
  title: string;
  desc: string;
}

interface FaqItem {
  q: string;
  a: string;
}

interface ProductSpec {
  material: string;
  size: string;
  weight: string;
  moq?: number;
}

interface ProductTranslationData {
  name?: string;
  description?: string;
  long_description?: string;
  specs?: ProductSpec;
  highlights?: HighlightItem[];
  faqs?: FaqItem[];
}

export interface ProductForTranslation {
  name: string;
  description: string;
  long_description?: string | null;
  moq?: number | null;
  specs?: Record<string, string> | null;
  highlights?: HighlightItem[] | null;
  faqs?: FaqItem[] | null;
  translations?: Record<string, ProductTranslationData> | null;
}

const SUPPORTED_LOCALES = ['en', 'ja', 'zh-TW', 'pt', 'es', 'nl'] as const;

function pickTranslation(
  product: ProductForTranslation,
  locale: string,
  field: keyof ProductTranslationData
): string | HighlightItem[] | FaqItem[] | ProductSpec | undefined {
  if (!SUPPORTED_LOCALES.includes(locale as typeof SUPPORTED_LOCALES[number])) {
    return undefined;
  }
  const t = product.translations?.[locale];
  if (!t) return undefined;
  return t[field];
}

export function useProductTranslation() {
  const { i18n } = useTranslation();

  const getProductName = (product: ProductForTranslation): string => {
    return (pickTranslation(product, i18n.language, 'name') as string) || product.name;
  };

  const getProductDescription = (product: ProductForTranslation): string => {
    return (pickTranslation(product, i18n.language, 'description') as string) || product.description;
  };

  const getProductLongDescription = (product: ProductForTranslation): string => {
    const translated = pickTranslation(product, i18n.language, 'long_description') as string;
    return translated || product.long_description || '';
  };

  const getProductSpecs = (product: ProductForTranslation): ProductSpec => {
    const translated = pickTranslation(product, i18n.language, 'specs') as ProductSpec | undefined;
    if (translated) return translated;
    const specs = product.specs;
    return {
      material: specs?.material || '',
      size: specs?.size || '',
      weight: specs?.weight || '',
      moq: specs?.moq != null ? Number(specs.moq) : product.moq != null ? Number(product.moq) : undefined,
    };
  };

  const getProductHighlights = (product: ProductForTranslation): HighlightItem[] => {
    const translated = pickTranslation(product, i18n.language, 'highlights') as HighlightItem[] | undefined;
    if (translated && translated.length > 0) return translated;
    return product.highlights || [];
  };

  const getProductFaqs = (product: ProductForTranslation): FaqItem[] => {
    const translated = pickTranslation(product, i18n.language, 'faqs') as FaqItem[] | undefined;
    if (translated && translated.length > 0) return translated;
    return product.faqs || [];
  };

  const needsTranslation = (product: ProductForTranslation): boolean => {
    return SUPPORTED_LOCALES.includes(i18n.language as typeof SUPPORTED_LOCALES[number]);
  };

  return {
    getProductName,
    getProductDescription,
    getProductLongDescription,
    getProductSpecs,
    getProductHighlights,
    getProductFaqs,
    needsTranslation,
    currentLang: i18n.language,
  };
}